import React, { useState, useEffect } from 'react';
import { paymentsApi, claimsApi } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { CurrencyInr, Plus, Receipt, TrendUp, Bank, CalendarBlank } from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(value || 0);
};

const PAYMENT_MODES = [
  { value: 'neft', label: 'NEFT' },
  { value: 'rtgs', label: 'RTGS' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'upi', label: 'UPI' },
  { value: 'cash', label: 'Cash' },
  { value: 'dd', label: 'Demand Draft' },
  { value: 'online', label: 'Online Transfer' },
];

const PaymentsPage = () => {
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [claims, setClaims] = useState([]);
  const [searchClaim, setSearchClaim] = useState('');
  const [form, setForm] = useState({
    claim_id: '',
    amount: '',
    payment_mode: 'neft',
    reference_number: '',
    payment_date: new Date().toISOString().split('T')[0],
    payer_reference: '',
    bank_name: '',
    deduction_amount: '0',
    deduction_reason: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [paymentsRes, statsRes] = await Promise.all([
        paymentsApi.list(),
        paymentsApi.stats()
      ]);
      setPayments(paymentsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const searchClaims = async (query) => {
    setSearchClaim(query);
    if (query.length < 2) { setClaims([]); return; }
    try {
      const res = await claimsApi.list({ search: query });
      setClaims(res.data?.slice(0, 10) || []);
    } catch { /* ignore */ }
  };

  const handleSubmit = async () => {
    if (!form.claim_id || !form.amount || !form.payment_date) {
      toast.error('Claim, amount and payment date are required');
      return;
    }
    try {
      await paymentsApi.create({
        ...form,
        amount: parseFloat(form.amount),
        deduction_amount: parseFloat(form.deduction_amount || '0')
      });
      toast.success('Payment posted successfully');
      setDialogOpen(false);
      setForm({
        claim_id: '', amount: '', payment_mode: 'neft', reference_number: '',
        payment_date: new Date().toISOString().split('T')[0],
        payer_reference: '', bank_name: '', deduction_amount: '0', deduction_reason: '', notes: ''
      });
      setSearchClaim('');
      setClaims([]);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post payment');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="payments-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payments-page">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800 tracking-tight font-['Outfit']" data-testid="payments-title">Payment Posting</h1>
          <p className="text-sm text-slate-500 mt-1">Record and track payments received against claims</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white shadow-lg shadow-emerald-500/25 gap-2" data-testid="new-payment-button">
              <Plus className="h-4 w-4" /> Post Payment
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Outfit']">Post New Payment</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <Label>Search Claim *</Label>
                <Input
                  placeholder="Type claim number or patient name..."
                  value={searchClaim}
                  onChange={(e) => searchClaims(e.target.value)}
                  className="border-slate-300 focus:border-sky-500"
                  data-testid="payment-search-claim"
                />
                {claims.length > 0 && (
                  <div className="mt-1 border border-slate-200 rounded-lg max-h-40 overflow-y-auto bg-white shadow-lg">
                    {claims.map(c => (
                      <button
                        key={c.id}
                        className="w-full text-left px-3 py-2 hover:bg-sky-50 text-sm border-b border-slate-100 last:border-b-0 transition-colors"
                        onClick={() => {
                          setForm(f => ({ ...f, claim_id: c.id }));
                          setSearchClaim(`${c.claim_number} - ${c.patient_name}`);
                          setClaims([]);
                        }}
                        data-testid={`claim-option-${c.id}`}
                      >
                        <span className="font-semibold text-sky-700">{c.claim_number}</span>
                        <span className="text-slate-500 ml-2">{c.patient_name}</span>
                        <span className="text-emerald-600 ml-2">{formatCurrency(c.billed_amount)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Amount Received *</Label>
                  <Input type="number" placeholder="0" value={form.amount}
                    onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                    data-testid="payment-amount" />
                </div>
                <div>
                  <Label>Payment Date *</Label>
                  <Input type="date" value={form.payment_date}
                    onChange={e => setForm(f => ({ ...f, payment_date: e.target.value }))}
                    data-testid="payment-date" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Payment Mode</Label>
                  <Select value={form.payment_mode} onValueChange={v => setForm(f => ({ ...f, payment_mode: v }))}>
                    <SelectTrigger data-testid="payment-mode-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PAYMENT_MODES.map(m => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Reference Number</Label>
                  <Input placeholder="UTR/Cheque No." value={form.reference_number}
                    onChange={e => setForm(f => ({ ...f, reference_number: e.target.value }))}
                    data-testid="payment-reference" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Bank Name</Label>
                  <Input placeholder="Bank" value={form.bank_name}
                    onChange={e => setForm(f => ({ ...f, bank_name: e.target.value }))}
                    data-testid="payment-bank" />
                </div>
                <div>
                  <Label>Payer Reference</Label>
                  <Input placeholder="Payer Ref" value={form.payer_reference}
                    onChange={e => setForm(f => ({ ...f, payer_reference: e.target.value }))}
                    data-testid="payment-payer-ref" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Deduction Amount</Label>
                  <Input type="number" placeholder="0" value={form.deduction_amount}
                    onChange={e => setForm(f => ({ ...f, deduction_amount: e.target.value }))}
                    data-testid="payment-deduction" />
                </div>
                <div>
                  <Label>Deduction Reason</Label>
                  <Input placeholder="Reason" value={form.deduction_reason}
                    onChange={e => setForm(f => ({ ...f, deduction_reason: e.target.value }))}
                    data-testid="payment-deduction-reason" />
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea placeholder="Additional notes..." value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  data-testid="payment-notes" />
              </div>
              <Button className="w-full bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white" onClick={handleSubmit} data-testid="submit-payment-button">
                Post Payment
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="payment-stats">
          <Card className="bg-white border border-slate-200 overflow-hidden">
            <CardContent className="pt-5 pb-4 relative">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">This Month Received</p>
                  <p className="text-2xl font-bold text-slate-800 mt-1 font-['Outfit']">{formatCurrency(stats.this_month?.total_received)}</p>
                </div>
                <div className="h-10 w-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <TrendUp className="h-5 w-5 text-emerald-600" />
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-400 to-green-500"></div>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 overflow-hidden">
            <CardContent className="pt-5 pb-4 relative">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Payments Count</p>
                  <p className="text-2xl font-bold text-slate-800 mt-1 font-['Outfit']">{stats.this_month?.count || 0}</p>
                </div>
                <div className="h-10 w-10 rounded-xl bg-sky-100 flex items-center justify-center">
                  <Receipt className="h-5 w-5 text-sky-600" />
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-sky-400 to-blue-500"></div>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 overflow-hidden">
            <CardContent className="pt-5 pb-4 relative">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Total Deductions</p>
                  <p className="text-2xl font-bold text-amber-600 mt-1 font-['Outfit']">{formatCurrency(stats.this_month?.total_deductions)}</p>
                </div>
                <div className="h-10 w-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <CurrencyInr className="h-5 w-5 text-amber-600" />
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 to-orange-500"></div>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 overflow-hidden">
            <CardContent className="pt-5 pb-4 relative">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Payment Modes</p>
                  <p className="text-2xl font-bold text-slate-800 mt-1 font-['Outfit']">{stats.by_mode?.length || 0}</p>
                </div>
                <div className="h-10 w-10 rounded-xl bg-violet-100 flex items-center justify-center">
                  <Bank className="h-5 w-5 text-violet-600" />
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-400 to-purple-500"></div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Payments Table */}
      <Card className="bg-white border border-slate-200 shadow-sm" data-testid="payments-table-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-medium font-['Outfit'] text-slate-800">Payment History</CardTitle>
        </CardHeader>
        <CardContent>
          {payments.length === 0 ? (
            <div className="text-center py-12 text-slate-500" data-testid="no-payments">
              <Receipt className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p className="text-sm">No payments recorded yet</p>
              <p className="text-xs mt-1">Click "Post Payment" to record the first payment</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Case No</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Claim No</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Patient</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">TPA / Insurance</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Amount</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Deduction</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Mode</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Reference</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Date</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Posted By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map((p, idx) => (
                    <TableRow key={p.id} className="hover:bg-sky-50/50 transition-colors" data-testid={`payment-row-${p.id}`}>
                      <TableCell className="font-semibold text-sm text-sky-700">{p.claim_number || '-'}</TableCell>
                      <TableCell className="text-xs font-mono text-orange-600">{p.insurance_claim_no || '-'}</TableCell>
                      <TableCell className="text-sm text-slate-600">{p.patient_name || '-'}</TableCell>
                      <TableCell className="text-sm text-slate-600 font-medium">{p.payer_name || '-'}</TableCell>
                      <TableCell className="text-sm font-semibold text-emerald-600">{formatCurrency(p.amount)}</TableCell>
                      <TableCell className="text-sm text-amber-600">{p.deduction_amount > 0 ? formatCurrency(p.deduction_amount) : '-'}</TableCell>
                      <TableCell>
                        <Badge className="bg-violet-100 text-violet-700 border-0 text-xs uppercase">{p.payment_mode}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">{p.reference_number || '-'}</TableCell>
                      <TableCell className="text-sm text-slate-500">{p.payment_date}</TableCell>
                      <TableCell className="text-sm text-slate-500">{p.posted_by_name || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentsPage;
